// Selected-tenant store (phase 6 §2) — which tenant the dashboard is
// operating on. The plane partitions every table by tenantId, stored in the
// table ATTRIBUTE still named "ownerEmail" (personal tenant = the user's
// email; team tenant = teamId uuid). The web app never sends the personal
// tenant explicitly: `null` here means "Personal" and the tenant is OMITTED
// from requests, so the plane resolves the caller's email — identical to
// pre-phase-6 behavior and backward compatible with older planes.

import { useCallback, useSyncExternalStore } from "react";

// localStorage key for the selected team tenant (contract §2: "Persist
// selection in localStorage"). Value is a teamId uuid; absent = Personal.
const TENANT_KEY = "nightswarm_tenant";

const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

/** Selected team tenantId (teamId uuid), or null for the personal tenant. */
export function getTenantId(): string | null {
  try {
    return localStorage.getItem(TENANT_KEY) || null;
  } catch {
    return null;
  }
}

/** Switch tenant — null returns to Personal. Notifies subscribers so every
 *  section refetches under the new tenant. */
export function setTenantId(tenantId: string | null): void {
  try {
    if (tenantId) localStorage.setItem(TENANT_KEY, tenantId);
    else localStorage.removeItem(TENANT_KEY);
  } catch {
    // storage unavailable — selection just won't persist
  }
  emit();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  // Cross-tab switches arrive as storage events.
  const onStorage = (e: StorageEvent) => {
    if (e.key === TENANT_KEY || e.key === null) fn();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(fn);
    window.removeEventListener("storage", onStorage);
  };
}

/** React binding for the selected tenant. Components that fetch
 *  tenant-scoped data should depend on `tenantId` so a switch refetches. */
export function useTenant(): {
  tenantId: string | null;
  setTenantId: (tenantId: string | null) => void;
} {
  const tenantId = useSyncExternalStore(subscribe, getTenantId);
  return { tenantId, setTenantId: useCallback(setTenantId, []) };
}
